export type BloodCrimesTvShortScene = {
  id: string;
  slug: string;
  name: string;
  character_slug: string | null;
  status: string;
  vertical_render: { status?: string; review_status?: string } | null;
};

export type BloodCrimesTvShortTrack = {
  id: string;
  title: string;
  genre: string | null;
  mood: string | null;
  approved: number;
  live_allowed: number;
  rights_status: string;
  verified_game_music_url: boolean;
  short_eligible: boolean;
};

type Template = {
  id: string;
  label: string;
  scene: (scene: BloodCrimesTvShortScene) => number;
  music: (track: BloodCrimesTvShortTrack) => number;
};

const contains = (value: string, terms: string[]) => terms.some((term) => value.toLowerCase().includes(term));

const templates: Template[] = [
  { id: 'mafia-queen-midnight-jazz', label: 'Mafia Queen — Midnight Jazz', scene: (s) => s.character_slug === 'mafia-queen' ? 10 : 0, music: (t) => contains(`${t.genre ?? ''} ${t.mood ?? ''}`, ['jazz','noir']) ? 10 : 0 },
  { id: 'night-club-dj-underground', label: 'Night Club DJ — Underground Beats', scene: (s) => s.character_slug === 'night-club-dj' ? 10 : 0, music: (t) => contains(`${t.genre ?? ''} ${t.mood ?? ''}`, ['industrial','electronic','underground']) ? 10 : 0 },
  { id: 'midnight-driver-night-drive', label: 'Midnight Driver — Night Drive', scene: (s) => s.character_slug === 'midnight-driver' ? 10 : 0, music: (t) => contains(`${t.genre ?? ''} ${t.mood ?? ''}`, ['synthwave','night drive']) ? 10 : 0 },
  { id: 'noir-jazz-singer-after-hours', label: 'Noir Jazz Singer — After Hours', scene: (s) => s.character_slug === 'noir-jazz-singer' ? 10 : 0, music: (t) => contains(`${t.genre ?? ''} ${t.mood ?? ''}`, ['jazz','lo-fi','lofi']) ? 10 : 0 },
  { id: 'rainy-mafia-city', label: 'Rainy Mafia City', scene: (s) => contains(`${s.slug} ${s.name}`, ['rain','street','harbor','city']) && !s.character_slug ? 10 : 0, music: (t) => contains(`${t.genre ?? ''} ${t.mood ?? ''}`, ['ambient','noir','lo-fi','lofi']) ? 10 : 0 },
  { id: 'mafia-queen-dark-luxury', label: 'Mafia Queen — Dark Luxury', scene: (s) => s.character_slug === 'mafia-queen' ? 10 : contains(`${s.slug} ${s.name}`, ['casino']) ? 5 : 0, music: (t) => contains(`${t.genre ?? ''} ${t.mood ?? ''}`, ['orchestral','cinematic','mafia','noir']) ? 10 : 0 },
];

export function getBloodCrimesTvShortTemplates(scenes: BloodCrimesTvShortScene[], tracks: BloodCrimesTvShortTrack[], now = new Date()) {
  const eligibleScenes = scenes.filter((scene) => scene.status === 'APPROVED' && scene.vertical_render?.status === 'READY' && scene.vertical_render.review_status === 'APPROVED');
  const eligibleTracks = tracks.filter((track) => track.approved === 1 && track.live_allowed === 1 && track.rights_status === 'APPROVED' && track.short_eligible);
  return templates.map((template) => {
    const scene = eligibleScenes.filter((item) => template.scene(item) > 0).sort((a, b) => template.scene(b) - template.scene(a) || a.id.localeCompare(b.id))[0] ?? null;
    const matchedTracks = eligibleTracks.filter((item) => template.music(item) > 0).sort((a, b) => template.music(b) - template.music(a) || a.id.localeCompare(b.id));
    // Stable weekly rotation spreads approved music without calling an LLM or changing render inputs.
    const mondayEpoch = Date.UTC(1970, 0, 5);
    const week = Math.floor((Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - mondayEpoch) / (7 * 24 * 60 * 60 * 1000));
    const music = matchedTracks.length ? matchedTracks[week % matchedTracks.length] ?? null : null;
    return { id: template.id, label: template.label, scene, music, available: Boolean(scene && music) };
  });
}
